import type { BetterAuthClientPlugin } from 'better-auth/client'

/**
 * Client counterpart of the server `socialRedirect()` plugin.
 *
 * Exposes `authClient.signIn.socialRedirect(...)`, which builds the URL of the
 * backend `/sign-in/social-redirect` endpoint from the client's configured
 * `baseURL`/`basePath` and returns it as a string.
 *
 * It intentionally does NOT navigate: the caller decides how to use the URL —
 * the main app does a top-level `window.location` navigation, the embeddable
 * widget opens it in a popup. Building the URL here (instead of a cross-origin
 * `signIn.social` XHR) keeps the OAuth `state` cookie first-party on the backend
 * origin and avoids the `state_mismatch` failure.
 */
export function socialRedirectClient() {
    return {
        id: 'social-redirect',
        // Deliberately no `$InferServerPlugin`. The server plugin adds no field
        // to `user` or `session`, it only registers two endpoints, and
        // `getActions` below types the one action we call. Declaring it makes
        // better-auth walk the server plugin's endpoint types while inferring
        // the session, which from 1.7 collapses `useSession().data` to `never`
        // in every consumer of the client. See docs/agents/gotchas.md.
        getActions: (_$fetch, _$store, options) => ({
            signIn: {
                socialRedirect: (opts: {
                    provider: string
                    callbackURL: string
                    errorCallbackURL?: string
                }): string => {
                    const baseURL = options?.baseURL ?? ''
                    const basePath = options?.basePath ?? 'auth'
                    const params = new URLSearchParams({
                        provider: opts.provider,
                        callbackURL: opts.callbackURL,
                    })
                    if (opts.errorCallbackURL) {
                        params.set('errorCallbackURL', opts.errorCallbackURL)
                    }
                    return `${baseURL}/${basePath}/sign-in/social-redirect?${params}`
                },
            },
        }),
    } satisfies BetterAuthClientPlugin
}
